'use client';

import SiteNav from '@/components/layout/SiteNav';
import SiteFooter from '@/components/layout/SiteFooter';
import { useState } from 'react';
import type { Dictionary, Locale } from '@/app/[lang]/dictionaries';

type FormState = {
  name: string;
  phone: string;
  email: string;
  subject: string;
  message: string;
};

type Status = 'idle' | 'loading' | 'success' | 'error';

const initialForm: FormState = {
  name: '',
  phone: '',
  email: '',
  subject: 'technical',
  message: '',
};

export default function LienHeClient({ lang, dict }: { lang: Locale; dict: Dictionary }) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const t = dict.lienHe;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'https://api.dev.mettaring.com';
      const res = await fetch(`${apiBase}/api/support-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setStatus('success');
        setForm(initialForm);
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data?.message || t.form.errorFallback);
        setStatus('error');
      }
    } catch {
      setErrorMsg(t.form.networkError);
      setStatus('error');
    }
  };

  return (
    <>
      <SiteNav lang={lang} dict={dict.nav} />

      <main className="bg-background min-h-screen">
        {/* Hero */}
        <section className="max-w-screen-xl mx-auto px-6 sm:px-12 pt-28 pb-16">
          <div className="max-w-3xl flex flex-col gap-6">
            <h1 className="font-headline font-extrabold text-[clamp(2.5rem,5vw,4.5rem)] leading-none tracking-[-0.05em] text-on-surface">
              {t.hero.title}
            </h1>
            <p className="text-lg sm:text-xl text-on-surface-variant leading-8 max-w-2xl">
              {t.hero.subtitle}
            </p>
          </div>
        </section>

        {/* Cards + Form */}
        <section className="max-w-screen-xl mx-auto px-6 sm:px-12 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">

            {/* ── Info sidebar ── */}
            <div className="flex flex-col gap-6">
              {/* Address */}
              <div className="bg-surface-container-low rounded-3xl p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[rgba(169,207,183,0.1)] flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary text-xl">location_on</span>
                  </div>
                  <div>
                    <p className="text-on-surface-variant font-headline font-bold text-xs tracking-[1.4px] uppercase mb-3">
                      {t.info.addressLabel}
                    </p>
                    <p className="text-on-surface font-headline font-bold text-xl sm:text-2xl leading-8">
                      {t.info.addressValue}
                    </p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-surface-container-low rounded-3xl p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[rgba(235,194,62,0.1)] flex items-center justify-center">
                    <span className="material-symbols-outlined text-tertiary text-xl">mail</span>
                  </div>
                  <div>
                    <p className="text-on-surface-variant font-headline font-bold text-xs tracking-[1.4px] uppercase mb-3">
                      {t.info.emailLabel}
                    </p>
                    <a
                      href={`mailto:${t.info.emailValue}`}
                      className="text-on-surface font-headline font-bold text-lg hover:text-primary transition-colors"
                    >
                      {t.info.emailValue}
                    </a>
                  </div>
                </div>
              </div>

              {/* Website */}
              <div className="bg-surface-container-low rounded-3xl p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[rgba(173,198,255,0.1)] flex items-center justify-center">
                    <span className="material-symbols-outlined text-secondary text-xl">language</span>
                  </div>
                  <div>
                    <p className="text-on-surface-variant font-headline font-bold text-xs tracking-[1.4px] uppercase mb-3">
                      {t.info.websiteLabel}
                    </p>
                    <a
                      href={t.info.websiteValue}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-on-surface font-headline font-bold text-lg hover:text-primary transition-colors"
                    >
                      {t.info.websiteValue}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Message form ── */}
            <div className="bg-surface-container-low rounded-[2rem] p-6 sm:p-8 flex flex-col gap-8 relative overflow-hidden">
              <div
                className="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none"
                style={{ background: 'rgba(169,207,183,0.05)', filter: 'blur(50px)' }}
              />

              <h2 className="font-headline font-bold text-2xl sm:text-3xl text-on-surface relative z-10">
                {t.form.heading}
              </h2>

              {/* Success banner */}
              {status === 'success' && (
                <div className="rounded-2xl bg-primary/10 border border-primary/30 px-6 py-4 text-primary font-body text-sm">
                  {t.form.successMessage}
                </div>
              )}

              {/* Error banner */}
              {status === 'error' && (
                <div className="rounded-2xl bg-error/10 border border-error/30 px-6 py-4 text-error font-body text-sm">
                  {errorMsg}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-6 relative z-10">
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[rgba(209,250,229,0.4)] font-headline font-bold text-xs tracking-[1.2px] uppercase">
                      {t.form.nameLabel}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder={t.form.namePlaceholder}
                      className="bg-surface-container-lowest rounded-2xl px-6 py-[18px] text-base text-on-surface placeholder:text-outline-variant outline-none border border-transparent focus:border-primary/40 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[rgba(209,250,229,0.4)] font-headline font-bold text-xs tracking-[1.2px] uppercase">
                      {t.form.phoneLabel}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={t.form.phonePlaceholder}
                      className="bg-surface-container-lowest rounded-2xl px-6 py-[18px] text-base text-on-surface placeholder:text-outline-variant outline-none border border-transparent focus:border-primary/40 transition-colors"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[rgba(209,250,229,0.4)] font-headline font-bold text-xs tracking-[1.2px] uppercase">
                      {t.form.emailLabel}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder={t.form.emailPlaceholder}
                      className="bg-surface-container-lowest rounded-2xl px-6 py-[18px] text-base text-on-surface placeholder:text-outline-variant outline-none border border-transparent focus:border-primary/40 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[rgba(209,250,229,0.4)] font-headline font-bold text-xs tracking-[1.2px] uppercase">
                      {t.form.subjectLabel}
                    </label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="bg-surface-container-lowest rounded-2xl px-6 py-[18px] text-base text-on-surface outline-none border border-transparent focus:border-primary/40 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="technical">{t.form.subjectOptions.technical}</option>
                      <option value="order">{t.form.subjectOptions.order}</option>
                      <option value="partnership">{t.form.subjectOptions.partnership}</option>
                      <option value="other">{t.form.subjectOptions.other}</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label className="text-[rgba(209,250,229,0.4)] font-headline font-bold text-xs tracking-[1.2px] uppercase">
                    {t.form.messageLabel}
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t.form.messagePlaceholder}
                    className="bg-surface-container-lowest rounded-2xl px-6 py-4 text-base text-on-surface placeholder:text-outline-variant outline-none border border-transparent focus:border-primary/40 transition-colors resize-none"
                  />
                </div>

                {/* Submit */}
                <div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="px-10 py-[14px] rounded-full bg-[linear-gradient(107.58deg,#A9CFB7_0%,#2D4F3C_100%)] text-on-primary font-headline font-bold text-lg hover:opacity-90 hover:scale-[1.02] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                  >
                    {status === 'loading' ? t.form.submitButtonLoading : t.form.submitButton}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter lang={lang} dict={dict.footer} />
    </>
  );
}
